/**
 * kits-cart-global.js
 *
 * Purpose: Cart drawer timers, full cart page timers, + smart recommendations.
 * Loaded globally via layout/theme.liquid so drawer timers appear on all pages;
 * cart page uses [data-ks-cart-timers-mount] in sections/main-cart.liquid.
 *
 * Timer 1: Booking countdown (15 min) — for Appointo items
 * Timer 2: Same-day dispatch countdown (before 12 PM) — for regular items
 * Smart Recs: Fetches related products from Shopify Recommendations API
 */

/* ═══════════════════════════════════════════════════════════════
   CART DRAWER TIMERS — Runs on ALL Pages
   ═══════════════════════════════════════════════════════════════ */

(function initCartTimers() {
  'use strict';

  var BOOKING_DURATION = 15 * 60;
  var BOOKING_CRITICAL = 5 * 60;
  var DISPATCH_HOUR = 12;
  var DISPATCH_URGENT = 2 * 60 * 60;
  var STORAGE_KEY = 'ks_booking_timer_start';
  var intervals = {
    drawerBooking: null,
    drawerDispatch: null,
    pageBooking: null,
    pageDispatch: null
  };

  function clearDrawerTimerIntervals() {
    if (intervals.drawerBooking) {
      clearInterval(intervals.drawerBooking);
      intervals.drawerBooking = null;
    }
    if (intervals.drawerDispatch) {
      clearInterval(intervals.drawerDispatch);
      intervals.drawerDispatch = null;
    }
  }

  function clearPageTimerIntervals() {
    if (intervals.pageBooking) {
      clearInterval(intervals.pageBooking);
      intervals.pageBooking = null;
    }
    if (intervals.pageDispatch) {
      clearInterval(intervals.pageDispatch);
      intervals.pageDispatch = null;
    }
  }

  function formatTime(seconds) {
    if (seconds < 0) seconds = 0;
    var hrs = Math.floor(seconds / 3600);
    var mins = Math.floor((seconds % 3600) / 60);
    var secs = seconds % 60;
    var pad = function(n) { return (n < 10 ? '0' : '') + n; };
    if (hrs > 0) return hrs + ':' + pad(mins) + ':' + pad(secs);
    return pad(mins) + ':' + pad(secs);
  }

  function hasBookingItems() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    var html = drawer.innerHTML;
    return (
      html.indexOf('Date &amp; Time') !== -1 ||
      html.indexOf('Date & Time') !== -1 ||
      html.indexOf('Date:') !== -1 ||
      html.indexOf('Appointment') !== -1
    );
  }

  function getSecondsUntilNoon() {
    var now = new Date();
    var noon = new Date();
    noon.setHours(DISPATCH_HOUR, 0, 0, 0);
    if (now >= noon) return -1;
    return Math.floor((noon - now) / 1000);
  }

  function isWeekend() {
    var day = new Date().getDay();
    return day === 0 || day === 6;
  }

  function getBookingStart() {
    var start = sessionStorage.getItem(STORAGE_KEY);
    if (!start) {
      start = Date.now();
      sessionStorage.setItem(STORAGE_KEY, String(start));
    }
    return parseInt(start, 10);
  }

  function createBookingTimer(suffix) {
    suffix = suffix || '';
    var el = document.createElement('div');
    el.className = 'ks-cart-timer ks-cart-timer--booking';
    el.id = 'ks-timer-booking' + suffix;
    el.innerHTML =
      '<svg class="ks-cart-timer__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<circle cx="12" cy="12" r="10"/>' +
        '<polyline points="12,6 12,12 16,14"/>' +
      '</svg>' +
      '<div class="ks-cart-timer__content">' +
        '<div class="ks-cart-timer__title">Complete your booking to secure your slot</div>' +
      '</div>' +
      '<span class="ks-cart-timer__time">15:00</span>';
    return el;
  }

  function createDispatchTimer(suffix) {
    suffix = suffix || '';
    var el = document.createElement('div');
    el.className = 'ks-cart-timer ks-cart-timer--dispatch';
    el.id = 'ks-timer-dispatch' + suffix;
    el.innerHTML =
      '<svg class="ks-cart-timer__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<rect x="1" y="3" width="15" height="13" rx="2"/>' +
        '<polygon points="16,8 20,8 23,11 23,16 16,16"/>' +
        '<circle cx="5.5" cy="18.5" r="2.5"/>' +
        '<circle cx="18.5" cy="18.5" r="2.5"/>' +
      '</svg>' +
      '<div class="ks-cart-timer__content">' +
        '<div class="ks-cart-timer__title">Order now for same-day dispatch</div>' +
        '<div class="ks-cart-timer__subtitle">Orders before 12PM ship today</div>' +
      '</div>' +
      '<span class="ks-cart-timer__time">--:--</span>';
    return el;
  }

  function updateBookingTimer(bookingId) {
    var el = document.getElementById(bookingId || 'ks-timer-booking');
    if (!el) return;

    var elapsed = Math.floor((Date.now() - getBookingStart()) / 1000);
    var remaining = Math.max(0, BOOKING_DURATION - elapsed);
    var timeEl = el.querySelector('.ks-cart-timer__time');
    var titleEl = el.querySelector('.ks-cart-timer__title');

    timeEl.textContent = formatTime(remaining);
    el.classList.remove('is-critical');

    if (remaining === 0) {
      titleEl.textContent = 'Your slot may no longer be available';
      if (bookingId && bookingId.indexOf('-page') !== -1) {
        clearInterval(intervals.pageBooking);
        intervals.pageBooking = null;
      } else {
        clearInterval(intervals.drawerBooking);
        intervals.drawerBooking = null;
      }
    } else if (remaining <= BOOKING_CRITICAL) {
      el.classList.add('is-critical');
      titleEl.textContent = 'Hurry! Complete your booking now';
    }
  }

  function updateDispatchTimer(dispatchId) {
    var el = document.getElementById(dispatchId || 'ks-timer-dispatch');
    if (!el) return;

    var remaining = getSecondsUntilNoon();
    var timeEl = el.querySelector('.ks-cart-timer__time');
    var titleEl = el.querySelector('.ks-cart-timer__title');
    var subtitleEl = el.querySelector('.ks-cart-timer__subtitle');

    el.classList.remove('is-urgent', 'is-missed');

    if (isWeekend()) {
      el.classList.add('is-missed');
      titleEl.textContent = 'Order now for Monday dispatch';
      subtitleEl.textContent = 'Weekend orders ship Monday';
      timeEl.textContent = 'MON';
    } else if (remaining < 0) {
      el.classList.add('is-missed');
      titleEl.textContent = 'Order now for next-day dispatch';
      subtitleEl.textContent = "Today's cutoff has passed";
      timeEl.textContent = 'TOMORROW';
    } else {
      timeEl.textContent = formatTime(remaining);
      if (remaining <= DISPATCH_URGENT) {
        el.classList.add('is-urgent');
        titleEl.textContent = 'Last chance for same-day dispatch!';
      } else {
        titleEl.textContent = 'Order now for same-day dispatch';
      }
    }
  }

  function initTimers() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var itemsContainer = drawer.querySelector('.cart-drawer__items');
    if (!itemsContainer) return;

    // Skip if cart is empty
    var isEmpty = drawer.querySelector('[class*="empty"]') ||
                  !drawer.querySelector('line-item, .line-item');
    if (isEmpty) return;

    // Clean up existing (drawer IDs only — no suffix)
    var oldBooking = document.getElementById('ks-timer-booking');
    var oldDispatch = document.getElementById('ks-timer-dispatch');
    if (oldBooking) oldBooking.remove();
    if (oldDispatch) oldDispatch.remove();
    clearDrawerTimerIntervals();

    // Get or create container
    var container = drawer.querySelector('.ks-timers-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'ks-timers-container';
      itemsContainer.parentNode.insertBefore(container, itemsContainer);
    }
    container.innerHTML = '';

    if (hasBookingItems()) {
      container.appendChild(createBookingTimer(''));
      updateBookingTimer('ks-timer-booking');
      intervals.drawerBooking = setInterval(function () {
        updateBookingTimer('ks-timer-booking');
      }, 1000);
    }
    // Same-day dispatch countdown intentionally disabled in the drawer (2026-07-31 request).
    // Booking/appointment countdown above is untouched — it holds a real fitting slot.
  }

  function initCartPageTimers() {
    var mount = document.querySelector('[data-ks-cart-timers-mount]');
    if (!mount) return;

    clearPageTimerIntervals();
    mount.innerHTML = '';

    var hasBooking = mount.getAttribute('data-ks-has-booking') === 'true';

    if (hasBooking) {
      mount.appendChild(createBookingTimer('-page'));
      updateBookingTimer('ks-timer-booking-page');
      intervals.pageBooking = setInterval(function () {
        updateBookingTimer('ks-timer-booking-page');
      }, 1000);
    } else {
      mount.appendChild(createDispatchTimer('-page'));
      updateDispatchTimer('ks-timer-dispatch-page');
      intervals.pageDispatch = setInterval(function () {
        updateDispatchTimer('ks-timer-dispatch-page');
      }, 1000);
    }
  }

  function setupObserver() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      setTimeout(setupObserver, 1000);
      return;
    }

    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'open' && mutations[i].target.hasAttribute('open')) {
          setTimeout(initTimers, 100);
          setTimeout(initSmartRecs, 200);
        }
      }
    }).observe(cartDrawer, { attributes: true });

    if (cartDrawer.hasAttribute('open')) {
      setTimeout(initTimers, 100);
      setTimeout(initSmartRecs, 200);
    }
  }

  document.addEventListener('cart:refresh', function() {
    setTimeout(initTimers, 100);
    setTimeout(initSmartRecs, 200);
  });
  document.addEventListener('cart:updated', function() {
    setTimeout(initTimers, 100);
    setTimeout(initSmartRecs, 200);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver);
  } else {
    setupObserver();
  }

  window.initTimers = initTimers;
  window.initCartPageTimers = initCartPageTimers;
  window.ksResetBookingTimer = function() {
    sessionStorage.removeItem(STORAGE_KEY);
    clearDrawerTimerIntervals();
    clearPageTimerIntervals();
    initTimers();
    initCartPageTimers();
  };

  function bootCartPageTimers() {
    if (document.querySelector('[data-ks-cart-timers-mount]')) {
      initCartPageTimers();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCartPageTimers);
  } else {
    bootCartPageTimers();
  }

  document.addEventListener('cart:refresh', function () {
    setTimeout(initCartPageTimers, 150);
  });
  document.addEventListener('cart:updated', function () {
    setTimeout(initCartPageTimers, 150);
  });
})();


/* ═══════════════════════════════════════════════════════════════
   SMART CART RECOMMENDATIONS
   Uses Shopify Recommendations API (intent=related) so
   BMW cart items get BMW recs, Mercedes gets Mercedes, etc.
   ═══════════════════════════════════════════════════════════════ */

function initSmartRecs() {
  'use strict';

  var drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  var cartItems = drawer.querySelectorAll('line-item, .line-item');
  if (!cartItems.length) return;

  // Extract product handles and IDs from cart
  var cartProductHandles = [];
  var firstProductId = null;

  cartItems.forEach(function(item) {
    var link = item.querySelector('a[href*="/products/"]');
    if (link) {
      var match = link.href.match(/\/products\/([^?/]+)/);
      if (match) cartProductHandles.push(match[1]);
    }
    // Try data attributes for product ID
    var pid = item.getAttribute('data-product-id') || item.dataset.productId;
    if (pid && !firstProductId) firstProductId = pid;
  });

  if (!firstProductId && !cartProductHandles.length) return;

  // Check if recs already rendered for this product
  var existingRecs = drawer.querySelector('.ks-cart-recs');
  if (existingRecs && existingRecs.dataset.forProduct === String(firstProductId)) return;

  // If we have a product ID, fetch recommendations directly
  if (firstProductId) {
    fetchAndRenderRecs(firstProductId, cartProductHandles, drawer);
    return;
  }

  // Otherwise get the product ID from the handle
  if (cartProductHandles.length) {
    fetch('/products/' + cartProductHandles[0] + '.json')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.product && data.product.id) {
          fetchAndRenderRecs(data.product.id, cartProductHandles, drawer);
        }
      })
      .catch(function() {});
  }
}

function fetchAndRenderRecs(productId, cartHandles, drawer) {
  var url = '/recommendations/products.json?product_id=' + productId +
            '&limit=6&intent=related';

  fetch(url)
    .then(function(res) { return res.json(); })
    .then(function(data) {
      var products = (data.products || []).filter(function(p) {
        return cartHandles.indexOf(p.handle) === -1 && p.available;
      });

      if (!products.length) return;

      renderSmartRecs(products.slice(0, 2), drawer, productId);
    })
    .catch(function() {});
}

function renderSmartRecs(products, drawer, forProductId) {
  var recsContainer = drawer.querySelector('.ks-cart-recs');

  if (!recsContainer) {
    recsContainer = document.createElement('div');
    recsContainer.className = 'ks-cart-recs';
    var footer = drawer.querySelector('.cart-drawer__footer, form');
    if (footer) {
      footer.parentNode.insertBefore(recsContainer, footer);
    } else {
      drawer.appendChild(recsContainer);
    }
  }

  recsContainer.dataset.forProduct = String(forProductId);

  var cardsHtml = products.map(function(product) {
    var image = product.featured_image || (product.images && product.images[0]) || '';
    // Use Shopify CDN resizing
    var imageUrl = image ? image.replace(/(\.[^.]+)$/, '_200x200$1') : '';
    var price = (product.price / 100).toFixed(2);
    var comparePrice = product.compare_at_price ? (product.compare_at_price / 100).toFixed(2) : null;
    var variantId = product.variants && product.variants[0] ? product.variants[0].id : '';
    var hasMultipleVariants = product.variants && product.variants.length > 1;

    return (
      '<article class="ks-cart-rec-card" data-product-id="' + product.id + '">' +
        '<a href="/products/' + product.handle + '" class="ks-rec__image">' +
          (imageUrl ? '<img class="ks-rec__img" src="' + imageUrl + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
          (comparePrice ? '<span class="ks-rec__badge">SALE</span>' : '') +
        '</a>' +
        '<div class="ks-rec__info">' +
          '<a href="/products/' + product.handle + '" class="ks-rec__name">' + product.title + '</a>' +
          '<div class="ks-rec__price-row">' +
            (comparePrice ? '<span class="ks-rec__price ks-rec__price--compare">&pound;' + comparePrice + '</span>' : '') +
            '<span class="ks-rec__price">&pound;' + price + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="ks-rec__action">' +
          (hasMultipleVariants
            ? '<a href="/products/' + product.handle + '" class="ks-rec__add-btn">Choose options</a>'
            : '<button type="button" class="ks-rec__add-btn" data-variant-id="' + variantId + '">Add +</button>'
          ) +
        '</div>' +
      '</article>'
    );
  }).join('');

  recsContainer.innerHTML =
    '<div class="ks-cart-recs__header">' +
      '<p class="ks-cart-recs__eyebrow">Frequently paired</p>' +
      '<p class="ks-cart-recs__title">Complete The Look</p>' +
    '</div>' +
    '<div class="ks-cart-recs__grid">' + cardsHtml + '</div>';

  // Bind add-to-cart buttons
  recsContainer.querySelectorAll('.ks-rec__add-btn[data-variant-id]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var variantId = this.dataset.variantId;
      if (!variantId) return;

      var button = this;
      button.textContent = 'Adding...';
      button.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
      .then(function(res) {
        if (res.ok) {
          button.textContent = 'Added \u2713';
          // Trigger Prestige theme cart refresh
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
        } else {
          button.textContent = 'Error';
        }
      })
      .catch(function() {
        button.textContent = 'Error';
      })
      .finally(function() {
        setTimeout(function() {
          button.textContent = 'Add +';
          button.disabled = false;
        }, 2000);
      });
    });
  });
}

window.initSmartRecs = initSmartRecs;


/* ═══════════════════════════════════════════════════════════════
   CART DRAWER ENHANCEMENT — Hormozi-Style Checkout
   Delivery estimation per line item, reordered buttons,
   urgency messaging, and trust signals.
   ═══════════════════════════════════════════════════════════════ */

(function initCartDrawerEnhancement() {
  'use strict';

  function enhanceCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    addDeliveryEstimation(drawer);
    reorderButtons(drawer);
    addScarcityNotice(drawer);
  }

  /* ─────────────────────────────────────────────────────────────
     Delivery Estimation per Line Item
     ───────────────────────────────────────────────────────────── */

  function addDeliveryEstimation(drawer) {
    var lineItems = drawer.querySelectorAll('line-item, .line-item');

    lineItems.forEach(function(item) {
      if (item.querySelector('.ks-delivery-estimate')) return;

      // Skip fitting appointment items — handled by Liquid appointment badge
      if (item.hasAttribute('data-fitting-appointment') || item.classList.contains('ks-line-item--fitting')) return;

      var itemText = item.innerHTML || '';

      var hasAppointment = /DATE\s*&amp;\s*TIME|DATE\s*&\s*TIME|Date\s*&\s*Time/i.test(itemText);
      var hasIncludedVariant = /\bINCLUDED\b/i.test(itemText);
      var hasFittingProperty = /fitting[:\s]+included/i.test(itemText);

      var isFittingItem = hasFittingProperty || (hasAppointment && hasIncludedVariant);
      if (isFittingItem) return;

      var isBookingItem = hasAppointment && !isFittingItem;

      var estimate = document.createElement('div');
      estimate.className = 'ks-delivery-estimate';

      if (isBookingItem) {
        var dateMatch = itemText.match(/(\d{1,2}\s+\w{3}\s+\d{4}|\w{3}\s+\d{1,2})/);
        var bookingDate = dateMatch ? dateMatch[0] : 'Your selected date';

        estimate.innerHTML =
          '<svg class="ks-delivery-estimate__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<rect x="3" y="4" width="18" height="18" rx="2"/>' +
            '<line x1="16" y1="2" x2="16" y2="6"/>' +
            '<line x1="8" y1="2" x2="8" y2="6"/>' +
            '<line x1="3" y1="10" x2="21" y2="10"/>' +
          '</svg>' +
          '<span>Fitting: <strong>' + bookingDate + '</strong></span>';
      } else {
        var today = new Date();
        var minDate = new Date(today);
        var maxDate = new Date(today);

        // Add 3 business days for min
        var daysToAdd = 3;
        while (daysToAdd > 0) {
          minDate.setDate(minDate.getDate() + 1);
          if (minDate.getDay() !== 0 && minDate.getDay() !== 6) daysToAdd--;
        }
        // Add 5 business days for max
        daysToAdd = 5;
        while (daysToAdd > 0) {
          maxDate.setDate(maxDate.getDate() + 1);
          if (maxDate.getDay() !== 0 && maxDate.getDay() !== 6) daysToAdd--;
        }

        var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        var fmtDate = function(d) { return days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()]; };

        estimate.innerHTML =
          '<svg class="ks-delivery-estimate__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<rect x="1" y="3" width="15" height="13" rx="2"/>' +
            '<polygon points="16,8 20,8 23,11 23,16 16,16"/>' +
            '<circle cx="5.5" cy="18.5" r="2.5"/>' +
            '<circle cx="18.5" cy="18.5" r="2.5"/>' +
          '</svg>' +
          '<span>Arrives: <strong>' + fmtDate(minDate) + ' \u2013 ' + fmtDate(maxDate) + '</strong></span>';
      }

      // Insert into the line item info area
      var infoSection = item.querySelector('.line-item-info, .line-item__info, [class*="info"]');
      if (infoSection) {
        infoSection.appendChild(estimate);
      } else {
        item.appendChild(estimate);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Reorder Buttons — Hormozi-Style Checkout on Top
     ───────────────────────────────────────────────────────────── */

  function reorderButtons(drawer) {
    var footer = drawer.querySelector('.cart-drawer__footer, .drawer__footer');
    if (!footer) return;

    // Skip if already enhanced
    if (footer.querySelector('.ks-cart-buttons')) return;

    // Extract current price from footer
    var priceEl = footer.querySelector('[class*="subtotal"], [class*="total"], .cart-drawer__button-price, sale-price');
    var priceText = '';
    if (priceEl) {
      var match = priceEl.textContent.match(/£[\d,.]+/);
      if (match) priceText = match[0];
    }

    // Create Hormozi-style button container
    var buttonContainer = document.createElement('div');
    buttonContainer.className = 'ks-cart-buttons';

    buttonContainer.innerHTML =
      '<button type="button" class="ks-checkout-btn">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
          '<rect x="3" y="11" width="18" height="11" rx="2"/>' +
          '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>' +
        '</svg>' +
        '<span class="ks-checkout-btn__text">SECURE YOUR ORDER</span>' +
        (priceText ? '<span class="ks-checkout-btn__price">' + priceText + '</span>' : '') +
        '<svg class="ks-checkout-btn__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
          '<line x1="5" y1="12" x2="19" y2="12"/>' +
          '<polyline points="12,5 19,12 12,19"/>' +
        '</svg>' +
      '</button>' +
      '<a href="/cart" class="ks-view-cart-btn">VIEW FULL CART</a>' +
      '<div class="ks-checkout-trust">' +
        '<span>\uD83D\uDD12 256-bit SSL</span>' +
        '<span>\u2713 Secure Payment</span>' +
      '</div>';

    // Move tax notice into Hormozi container (bottom)
    var taxNotice = footer.querySelector('p.text-subdued');
    if (taxNotice) {
      taxNotice.className = 'ks-tax-notice';
      buttonContainer.appendChild(taxNotice);
    }

    // Hide old button group
    var oldButtonGroup = footer.querySelector('.button-group');
    if (oldButtonGroup) oldButtonGroup.style.display = 'none';

    // Hide old standalone buttons
    var oldCheckoutForm = footer.querySelector('form[action*="checkout"]');
    if (oldCheckoutForm) oldCheckoutForm.style.display = 'none';
    var oldViewCart = footer.querySelector(':scope > a[href="/cart"]');
    if (oldViewCart) oldViewCart.style.display = 'none';

    // Insert new buttons
    footer.appendChild(buttonContainer);

    // Checkout click handler — submit existing form or navigate
    var newCheckoutBtn = buttonContainer.querySelector('.ks-checkout-btn');
    newCheckoutBtn.addEventListener('click', function(e) {
      e.preventDefault();

      var form = drawer.querySelector('form[action*="checkout"]') ||
                 document.querySelector('form[action*="checkout"]');
      if (form) {
        form.submit();
      } else {
        window.location.href = '/checkout';
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Scarcity Notice
     ───────────────────────────────────────────────────────────── */

  function addScarcityNotice(drawer) {
    var itemCount = drawer.querySelectorAll('line-item, .line-item').length;
    if (itemCount === 0) return;

    var itemsContainer = drawer.querySelector('.cart-drawer__items');
    if (!itemsContainer || itemsContainer.querySelector('.ks-scarcity-notice')) return;

    var notice = document.createElement('div');
    notice.className = 'ks-scarcity-notice';
    notice.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">' +
        '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>' +
      '</svg>' +
      '<span>' + itemCount + ' item' + (itemCount > 1 ? 's' : '') + ' in your cart \u2014 Don\u2019t lose them!</span>';

    itemsContainer.insertBefore(notice, itemsContainer.firstChild);
  }

  /* ─────────────────────────────────────────────────────────────
     Observer — Re-run on cart drawer open & cart updates
     ───────────────────────────────────────────────────────────── */

  function setupHormoziObserver() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      setTimeout(setupHormoziObserver, 1000);
      return;
    }

    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'open' && mutations[i].target.hasAttribute('open')) {
          setTimeout(enhanceCartDrawer, 250);
        }
      }
    });

    observer.observe(drawer, { attributes: true, childList: true, subtree: true });

    document.addEventListener('cart:refresh', function() {
      // Remove old enhancements so they rebuild with fresh data
      var old = drawer.querySelector('.ks-cart-buttons');
      if (old) old.remove();
      var oldNotice = drawer.querySelector('.ks-scarcity-notice');
      if (oldNotice) oldNotice.remove();
      drawer.querySelectorAll('.ks-delivery-estimate').forEach(function(el) { el.remove(); });

      // Re-show old button group temporarily (it gets re-hidden by reorderButtons)
      var bg = drawer.querySelector('.button-group');
      if (bg) bg.style.display = '';

      setTimeout(enhanceCartDrawer, 300);
    });

    document.addEventListener('cart:updated', function() {
      var old = drawer.querySelector('.ks-cart-buttons');
      if (old) old.remove();
      var oldNotice = drawer.querySelector('.ks-scarcity-notice');
      if (oldNotice) oldNotice.remove();
      drawer.querySelectorAll('.ks-delivery-estimate').forEach(function(el) { el.remove(); });

      var bg = drawer.querySelector('.button-group');
      if (bg) bg.style.display = '';

      setTimeout(enhanceCartDrawer, 300);
    });

    if (drawer.hasAttribute('open')) {
      enhanceCartDrawer();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupHormoziObserver);
  } else {
    setupHormoziObserver();
  }
})();
